import type { NetMessage, PeerId } from './transport';
import type {
  BuffSnapshot,
  EntitySnapshot,
  FloorMessage,
  SnapshotMessage,
} from './replication';
import { dungeonService } from '../services/DungeonService';
import { DungeonEntityType, type DirectionEnum } from '../types/enums';
import { PlayerEntity } from '../entities/PlayerEntity';
import { BUFF_REGISTRY } from '../data/buffs.config';
import type { ActiveBuff } from '../types/buff.types';
import { runStartProgressionService } from '../services/RunStartProgressionService';
import type { DungeonEntity } from '../entities/DungeonEntity';
import { lastSentMoveSeq, setHostAckedMoveSeq } from './inputSync';
import { projectileService } from '../services/ProjectileService';
import { animationService } from '../services/AnimationService';
import { msToTicks } from '../utils/timing.utils';
import {
  isCombatEvent,
  type ProjectileSpawnEvent,
  type ProjectileDespawnEvent,
  type DamageNumberEvent,
  type VfxEvent,
  type VfxDespawnEvent,
  type PreviewAddEvent,
  type PreviewRemoveEvent,
  type CastRejectedEvent,
} from './combatEvents';

interface BufferedSnapshot {
  t: number;
  entities: Map<string, EntitySnapshot>;
}

const INTERP_DELAY_MS = 100;
const MAX_BUFFER = 30;

// Local-player reconcile: glide (vs hard teleport) over ~the snapshot interval
// when the divergence is small, so host-driven motion the client doesn't predict
// (dash/knockback) reads smoothly instead of snapping once per 20 Hz snapshot.
// A larger gap (warp teleport) still snaps.
const RECONCILE_GLIDE_TICKS = msToTicks(80);
const RECONCILE_GLIDE_MAX_CELLS = 3;

export class ReplicationClient {
  private buffer: BufferedSnapshot[] = [];
  private lastSeq = -1;
  private onFloor: (floor: FloorMessage) => void;

  constructor(onFloor: (floor: FloorMessage) => void) {
    this.onFloor = onFloor;
  }

  handle(_from: PeerId, msg: NetMessage): void {
    if (msg.type === 'floor') {
      this.onFloor(msg as FloorMessage);
      return;
    }
    if (msg.type === 'snapshot') {
      const snap = msg as SnapshotMessage;
      // Latest-wins on the unreliable channel: a reordered or duplicated snapshot
      // (seq <= last applied) would push the buffer out of monotonic order and
      // roll reconcile/interpolate backward. Drop it.
      if (snap.seq <= this.lastSeq) {
        return;
      }
      this.lastSeq = snap.seq;
      const entities = new Map<string, EntitySnapshot>();
      for (const e of snap.entities) {
        entities.set(e.id, e);
      }
      this.buffer.push({ t: snap.t, entities });
      if (this.buffer.length > MAX_BUFFER) {
        this.buffer.shift();
      }
      this.syncEntitySet(snap.entities);
      this.reconcileLocalPlayer(entities);
      return;
    }
    if (isCombatEvent(msg)) {
      this.applyCombatEvent(msg);
    }
  }

  private applyCombatEvent(msg: NetMessage): void {
    switch (msg.type) {
      case 'projectile-spawn': {
        const e = msg as ProjectileSpawnEvent;
        projectileService.addClientProjectile({
          id: e.id,
          sprite: e.sprite,
          originX: e.originX,
          originY: e.originY,
          direction: e.direction as DirectionEnum,
          speed: e.speed,
          range: e.range,
        });
        return;
      }
      case 'projectile-despawn': {
        projectileService.removeClientProjectile(
          (msg as ProjectileDespawnEvent).id,
        );
        return;
      }
      case 'vfx': {
        const e = msg as VfxEvent;
        animationService.createAnimation(e.spriteName, e.x, e.y, e.duration, {
          id: e.id,
          direction: e.direction as DirectionEnum | undefined,
          isLooping: e.isLooping,
          animationSpeed: e.animationSpeed,
          width: e.width,
          height: e.height,
          scale: e.scale,
          anchor: e.anchor,
          rotation: e.rotation,
          parallaxDepth: e.parallaxDepth,
        });
        return;
      }
      case 'vfx-despawn': {
        animationService.removeSpellAnimation((msg as VfxDespawnEvent).id);
        return;
      }
      case 'damage-number': {
        const e = msg as DamageNumberEvent;
        const ent = dungeonService.getEntityById(e.targetId);
        if (!ent) {
          return;
        }
        if (e.display === 'healHp' || e.display === 'healShield') {
          ent.pushHealDisplay(e.value, e.worldX, e.worldY, e.display);
        } else {
          ent.pushDamageDisplay(
            e.value,
            e.worldX,
            e.worldY,
            e.display,
            e.crit,
            e.element,
          );
        }
        return;
      }
      case 'preview-add': {
        const e = msg as PreviewAddEvent;
        const preview = dungeonService.previewService;
        // Own telegraph: reconcile the predicted group onto the host's authoritative
        // cells, KEEPING the predicted timer (anchored to the local cast) — recreating
        // with full duration would linger ~RTT too long.
        if (
          e.entityId === dungeonService.localPlayerId &&
          e.castSeq !== undefined
        ) {
          const predictedGroup = preview.takePredictedGroup(e.castSeq);
          const elapsed =
            predictedGroup !== undefined
              ? preview.getGroupElapsed(predictedGroup)
              : undefined;
          if (predictedGroup !== undefined) {
            preview.removeAffectedTilesByGroup(predictedGroup);
          }
          for (const cell of e.cells) {
            preview.createAffectedTile(cell.x, cell.y, {
              entityId: e.entityId,
              isPlayer: e.isPlayer,
              groupId: e.groupId,
              duration: e.duration,
              type: e.previewType,
              color: e.color,
              alpha: e.alpha,
              noProgressFill: e.noProgressFill,
            });
          }
          if (elapsed !== undefined) {
            preview
              .getAffectedTilesByGroup(e.groupId)
              .forEach((t) => (t.elapsedTime = elapsed));
          }
          return;
        }
        // Other entities (monsters / other players): stack a fresh telegraph.
        for (const cell of e.cells) {
          preview.createAffectedTile(cell.x, cell.y, {
            entityId: e.entityId,
            isPlayer: e.isPlayer,
            groupId: e.groupId,
            duration: e.duration,
            type: e.previewType,
            color: e.color,
            alpha: e.alpha,
            noProgressFill: e.noProgressFill,
          });
        }
        return;
      }
      case 'preview-remove': {
        const e = msg as PreviewRemoveEvent;
        if (e.groupId) {
          dungeonService.previewService.removeAffectedTilesByGroup(e.groupId);
        } else if (e.entityId) {
          dungeonService.previewService.removeAffectedTilesByEntity(e.entityId);
        }
        return;
      }
      case 'cast-rejected': {
        const e = msg as CastRejectedEvent;
        if (e.entityId !== dungeonService.localPlayerId) {
          return;
        }
        const groupId = dungeonService.previewService.takePredictedGroup(
          e.castSeq,
        );
        if (groupId) {
          dungeonService.previewService.removeAffectedTilesByGroup(groupId);
        }
        return;
      }
    }
  }

  /**
   * The local player is client-predicted, not interpolated. Take its
   * authoritative life from the host every snapshot; correct its position only
   * when the predicted logical cell diverges AND it is idle (a rejected or
   * desynced step) — snapping mid-move would stutter normal play.
   */
  private reconcileLocalPlayer(entities: Map<string, EntitySnapshot>): void {
    const localId = dungeonService.localPlayerId;
    if (!localId) {
      return;
    }
    const ent = dungeonService.getEntityById(localId);
    const snap = entities.get(localId);
    if (!ent || !snap) {
      return;
    }
    ent.life = snap.life;
    ent.maxLife = snap.maxLife;
    ent.reconcileCooldowns(snap.cooldowns ?? []);
    // Display the local player's host-side buffs in the HUD (BuffList reads the
    // entity's active buffs). No recompute: the local player's stats stay
    // host-authoritative for prediction (cf. tickPrediction).
    syncEntityBuffs(ent, snap.buffs, false);
    setHostAckedMoveSeq(snap.ackSeq);
    // Only snap once the host has processed all of this client's inputs:
    // otherwise the prediction (legitimately ahead) is rolled back to a stale
    // snapshot, which rubber-bands. A genuine rejected step stays diverged after
    // the ack catches up and is corrected then.
    const acked = snap.ackSeq >= lastSentMoveSeq();
    const diverged = ent.positionX !== snap.x || ent.positionY !== snap.y;
    if (acked && diverged && !ent.getActionState()) {
      const cells = Math.max(
        Math.abs(ent.positionX - snap.x),
        Math.abs(ent.positionY - snap.y),
      );
      if (cells <= RECONCILE_GLIDE_MAX_CELLS) {
        // Continuous host-driven step the client doesn't predict (dash,
        // knockback): glide to the cell so it isn't a per-snapshot teleport on
        // our screen, yet always settles grid-aligned.
        ent.glideToCell(snap.x, snap.y, RECONCILE_GLIDE_TICKS);
      } else {
        // Large jump (warp teleport, respawn): snap instantly.
        ent.teleportTo(snap.x, snap.y);
      }
    }
  }

  /** Create/remove dungeon entities so the set matches the latest snapshot, and
   *  sync each entity's replicated buffs (display). */
  private syncEntitySet(snapEntities: EntitySnapshot[]): void {
    // Freshest logical cells for move prediction (cf. dungeonService.clientFreshCells):
    // the entity's live positionX/Y is interpolated (~100 ms behind), so collision
    // would miss an entity that just entered a cell on the host.
    const fresh = dungeonService.clientFreshCells;
    fresh.clear();
    for (const e of snapEntities) {
      fresh.set(`${e.x},${e.y}`, e.id);
    }
    const present = new Set(snapEntities.map((e) => e.id));
    for (const ent of [...dungeonService.getEntities()]) {
      if (!present.has(ent.id)) {
        dungeonService.removeEntity(ent.id);
      }
    }
    const localId = dungeonService.localPlayerId;
    for (const e of snapEntities) {
      let ent = dungeonService.getEntityById(e.id);
      if (!ent) {
        const created = reconstructEntity(e);
        if (!created) {
          continue;
        }
        dungeonService.addEntity(created);
        ent = created;
      }
      // The local player's buffs are synced (without recompute) in
      // reconcileLocalPlayer; everything else recomputes so size/opacity apply.
      if (ent.id !== localId) {
        syncEntityBuffs(ent, e.buffs, true);
      }
    }
  }

  /** Interpolate visual positions toward `renderTime = hostNow - delay`. */
  interpolate(hostNow: number): void {
    const renderTime = hostNow - INTERP_DELAY_MS;
    let older: BufferedSnapshot | null = null;
    let newer: BufferedSnapshot | null = null;
    for (const s of this.buffer) {
      if (s.t <= renderTime) {
        older = s;
      } else {
        newer = s;
        break;
      }
    }
    if (!older) {
      return;
    }
    const a = older;
    const b = newer ?? older;
    const span = b.t - a.t || 1;
    const alpha = Math.min(1, Math.max(0, (renderTime - a.t) / span));
    for (const ent of dungeonService.getEntities()) {
      if (ent.id === dungeonService.localPlayerId) {
        continue;
      }
      const ea = a.entities.get(ent.id);
      const eb = b.entities.get(ent.id) ?? ea;
      if (!ea || !eb) {
        continue;
      }
      applyInterpolated(ent, ea, eb, alpha);
    }
  }
}

function buffsFromSnapshot(snaps: BuffSnapshot[] | undefined): ActiveBuff[] {
  if (!snaps) {
    return [];
  }
  const out: ActiveBuff[] = [];
  for (const s of snaps) {
    const def = BUFF_REGISTRY[s.id];
    if (!def) {
      continue;
    }
    out.push({ def, remainingTicks: s.remainingTicks, stacks: s.stacks });
  }
  return out;
}

/** Apply the host-replicated buff set to an entity for display. Wholesale replace
 *  (the snapshot is the full truth) — so a buff the host dropped (antidote, dispel,
 *  expiry) is simply absent and gets cleared here. The only early-out is the
 *  no-buffs-either-side case (the common plain monster), which avoids a pointless
 *  recompute. */
function syncEntityBuffs(
  ent: DungeonEntity,
  snaps: BuffSnapshot[] | undefined,
  recompute: boolean,
): void {
  const hasNow = ent.getActiveBuffs().length > 0;
  const hasNew = !!snaps && snaps.length > 0;
  if (!hasNow && !hasNew) {
    return;
  }
  ent.setReplicatedBuffs(buffsFromSnapshot(snaps), recompute);
}

function applySnapshotFields(ent: DungeonEntity, e: EntitySnapshot): void {
  ent.id = e.id;
  ent.maxLife = e.maxLife;
  ent.life = e.life;
  ent.absoluteX = e.ax;
  ent.absoluteY = e.ay;
  ent.direction = e.direction as DirectionEnum;
  ent.currentSpriteName = e.sprite;
}

function reconstructEntity(e: EntitySnapshot): DungeonEntity | null {
  if (e.type === DungeonEntityType.PLAYER) {
    const runStats = runStartProgressionService.buildRunStats();
    const player = new PlayerEntity({
      life: e.life,
      maxLife: e.maxLife,
      isWalkableOver: false,
      configId: e.configId,
      x: e.x,
      y: e.y,
      stats: runStats.stats,
    });
    applySnapshotFields(player, e);
    return player;
  }
  if (e.type === DungeonEntityType.MONSTER) {
    const monster = dungeonService.monsterSpawningService.createMonster(
      e.configId,
      e.x,
      e.y,
      dungeonService.getCurrentFloorLevel(),
    );
    if (!monster) {
      return null;
    }
    applySnapshotFields(monster, e);
    // `createMonster` sets mapIcon from config only; elite/miniboss/boss markers
    // are applied post-spawn on the host, so mirror the replicated value.
    if (e.mapIcon) {
      monster.mapIcon = e.mapIcon;
    }
    return monster;
  }
  return null;
}

function applyInterpolated(
  ent: DungeonEntity,
  a: EntitySnapshot,
  b: EntitySnapshot,
  alpha: number,
): void {
  ent.positionX = b.x;
  ent.positionY = b.y;
  ent.direction = b.direction as DirectionEnum;
  ent.life = b.life;
  ent.maxLife = b.maxLife;
  ent.currentSpriteName = b.sprite;
  ent.absoluteX = a.ax + (b.ax - a.ax) * alpha;
  ent.absoluteY = a.ay + (b.ay - a.ay) * alpha;
  ent.markDirty();
}
